import React, { useEffect, useState } from "react";
import "./index.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import LoginForm from "./components/auth/LoginForm";
import Patients from "./pages/Patients";
import GeneralTransactionsPage from "./pages/GeneralTransactions";
import Appointments from "./pages/Appointments";
import MedicalServices from "./pages/MedicalServices";
import RevenueDashboard from "./pages/RevenueDashboard";
import PerfectDashboard from "./pages/PerfectDashboard";
import SecretaryManagement from "./pages/StaffManagement";
import colorTheme from "./components/common/color";
import UserSettingsModal from "./components/UserSettings/UserSettingsModal";

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Add state for settings modal
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  useEffect(() => {
    // Disable autocomplete on all existing and future form elements
    const disableAutocomplete = () => {
      document.querySelectorAll("input, select, textarea").forEach((el) => {
        el.setAttribute("autocomplete", "off");
        el.setAttribute("data-form-type", "other");
      });
    };

    disableAutocomplete();

    // Run again after any DOM changes
    const observer = new MutationObserver(disableAutocomplete);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);
  if (isLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #E0F7FA 0%, #80DEEA 100%)",
          padding: "16px",
        }}
      >
        <div
          style={{
            background: "#ffffff",
            borderRadius: "16px",
            padding: "32px",
            textAlign: "center",
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
            width: "100%",
            maxWidth: "400px",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #e2e8f0",
              borderTop: "4px solid #3b82f6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px auto",
            }}
          />
          <p>Loading Caduceus...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginForm />;
  }

  // Get current page from pathname
  const getCurrentPage = () => {
    const path = location.pathname;
    if (path.startsWith("/appointments")) return "appointments";
    if (path.startsWith("/patients")) return "patients";
    if (path.startsWith("/services")) return "services";
    if (path.startsWith("/revenue")) return "revenue";
    if (path.startsWith("/transactions")) return "transactions";
    if (path.startsWith("/Secretary")) return "SecretaryManagement";
    if (path === "/") return "dashboard";
    return "dashboard";
  };

  const currentPage = getCurrentPage();

  // Navigation items based on user role
  const getNavigationItems = () => {
    const items = [
      { id: "dashboard", label: "Dashboard", icon: "🏠", path: "/" },
      {
        id: "appointments",
        label: "Appointments",
        icon: "📅",
        path: "/appointments",
      },
      { id: "patients", label: "Patients", icon: "👥", path: "/patients" },
    ];

    // Get user roles array
    const userRoles = user?.roles || [];

    // Admin gets everything
    if (userRoles.includes("admin")) {
      items.push(
        {
          id: "revenue",
          label: "Revenue",
          icon: "💰",
          path: "/revenue",
        },
        {
          id: "transactions",
          label: "Transactions",
          icon: "💳",
          path: "/transactions",
        },
        { id: "services", label: "Services", icon: "⚕️", path: "/services" },
        {
          id: "secretarymanagement",
          label: "Users",
          icon: "👥",
          path: "/SecretaryManagement",
        }
      );
    }
    // Doctor gets revenue (but not transactions)
    else if (userRoles.includes("doctor")) {
      items.push({
        id: "revenue",
        label: "Revenue",
        icon: "💰",
        path: "/revenue",
      });
    }
    // Secretary gets transactions (but not revenue)
    else if (userRoles.includes("secretary")) {
      items.push({
        id: "transactions",
        label: "Transactions",
        icon: "💳",
        path: "/transactions",
      });
    }

    return items;
  };

  const navigationItems = getNavigationItems();

  const navItemStyle = (isActive: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 16px",
    background: isActive ? "#3b82f6" : "transparent",
    color: isActive ? "#ffffff" : "#64748b",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all 0.2s ease",
    textDecoration: "none",
    width: "100%",
    justifyContent: "flex-start",
    minHeight: "44px",
  });

  const handleNavClick = (path: string) => {
    navigate(path);
  };

  const toggleMobileMenu = () => {
    const panel = document.querySelector(".mobile-panel") as HTMLElement;
    const backdrop = document.querySelector(".mobile-backdrop") as HTMLElement;
    const button = document.querySelector(".mobile-menu-btn") as HTMLElement;

    if (panel && backdrop && button) {
      const isHidden = panel.style.display === "none" || !panel.style.display;

      if (isHidden) {
        // Show menu
        panel.style.display = "block";
        backdrop.style.display = "block";
        button.innerHTML = "✕";
        // Prevent body scroll when menu is open
        document.body.style.overflow = "hidden";
      } else {
        // Hide menu
        panel.style.display = "none";
        backdrop.style.display = "none";
        button.innerHTML = "☰";
        // Restore body scroll
        document.body.style.overflow = "";
      }
    }
  };

  const closeMobileMenu = () => {
    const panel = document.querySelector(".mobile-panel") as HTMLElement;
    const backdrop = document.querySelector(".mobile-backdrop") as HTMLElement;
    const button = document.querySelector(".mobile-menu-btn") as HTMLElement;

    if (panel && backdrop && button) {
      panel.style.display = "none";
      backdrop.style.display = "none";
      button.innerHTML = "☰";
      document.body.style.overflow = "";
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        position: "relative",
      }}
    >
      {/* Mobile Backdrop */}
      <div
        className="mobile-backdrop"
        style={{
          display: "none",
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "rgba(0, 0, 0, 0.5)",
          zIndex: 998,
        }}
        onClick={closeMobileMenu}
      />

      {/* Navigation - Following Headless UI Pattern */}
      <nav
        style={{
          background: "#ffffff",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
          position: "relative",
          zIndex: 997,
        }}
      >
        <div
          style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 16px" }}
        >
          <div
            style={{
              position: "relative",
              display: "flex",
              height: "64px",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* Mobile menu button - Left side */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
              }}
              className="mobile-menu-btn-container tabs-container"
            >
              <button
                onClick={toggleMobileMenu}
                className="mobile-menu-btn"
                style={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                  padding: "10px",
                  color: "#374151",
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  cursor: "pointer",
                  fontSize: "16px",
                  height: "40px",
                  width: "40px",
                  transition: "all 0.2s ease",
                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#f3f4f6";
                  e.currentTarget.style.borderColor = "#d1d5db";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#f9fafb";
                  e.currentTarget.style.borderColor = "#e5e7eb";
                }}
              >
                ☰
              </button>
            </div>

            {/* Centered Logo on Mobile, Left-aligned on Desktop */}
            <div
              style={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
              }}
              className="mobile-centered-logo"
            >
              <h1
                style={{
                  margin: 0,
                  color: "#1e293b",
                  fontSize: "20px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
                onClick={() => navigate("/")}
              >
                Caduceus
              </h1>
            </div>

            {/* Desktop Logo and Nav - Left side */}
            <div
              style={{
                display: "flex",
                flex: 1,
                alignItems: "center",
                justifyContent: "flex-start",
              }}
              className="desktop-nav-container"
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "32px" }}
              >
                {/* Desktop Logo */}
                <h1
                  style={{
                    margin: 0,
                    color: "#1e293b",
                    fontSize: "20px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                  className="desktop-logo"
                  onClick={() => navigate("/")}
                >
                  Caduceus
                </h1>

                {/* Desktop Navigation */}
                <div
                  style={{ display: "flex", gap: "8px" }}
                  className="desktop-nav"
                >
                  {navigationItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleNavClick(item.path)}
                      style={{
                        ...navItemStyle(currentPage === item.id),
                        width: "auto",
                        padding: "8px 16px",
                      }}
                      onMouseEnter={(e) => {
                        if (currentPage !== item.id) {
                          e.currentTarget.style.background = "#f1f5f9";
                          e.currentTarget.style.color = "#1e293b";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (currentPage !== item.id) {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.color = "#64748b";
                        }
                      }}
                    >
                      <span style={{ fontSize: "16px" }}>{item.icon}</span>
                      <span className="nav-label">{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right side - User info and settings */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
              className="desktop-user-section"
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  fontSize: "13px",
                }}
                className="desktop-user-info"
              >
                <span style={{ color: "#1e293b", fontWeight: "500" }}>
                  {user?.first_name} {user?.last_name}
                </span>
                <span style={{ color: "#64748b", fontSize: "12px" }}>
                  {user?.roles?.[0] || user?.role || "User"} • Clinic:{" "}
                  {user?.clinic_id || "Main"}
                </span>
              </div>

              {/* Settings Button */}
              <button
                onClick={() => setIsSettingsOpen(true)}
                style={{
                  padding: "8px 16px",
                  background: colorTheme.primary,
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: "500",
                  transition: "all 0.2s ease",
                  minHeight: "40px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
                className="desktop-settings-btn"
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#1e40af";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = colorTheme.primary;
                }}
              >
                <span style={{ fontSize: "14px" }}>⚙️</span>
                Settings
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Panel - Overlay */}
      <div
        className="mobile-panel nn"
        style={{
          flexDirection: "row",
          display: "none",
          position: "fixed",
          top: "64px",
          left: 0,
          width: "100%",
          maxWidth: "400px",
          height: "calc(100vh - 64px)",
          background: "#ffffff",
          boxShadow: "2px 0 10px rgba(0, 0, 0, 0.1)",
          zIndex: 999,
          overflowY: "auto",
          padding: "16px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            padding: "8px 0 12px 0",
          }}
        >
          {navigationItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                handleNavClick(item.path);
                closeMobileMenu();
              }}
              className="nn"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "12px 16px",
                background: currentPage === item.id ? "#1f2937" : "transparent",
                color: currentPage === item.id ? "#ffffff" : "#374151",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: "500",
                width: "100%",
                justifyContent: "flex-start",
                minHeight: "48px",
              }}
            >
              <span style={{ fontSize: "18px" }}>{item.icon}</span>
              {item.label}
            </button>
          ))}

          {/* Mobile User Section */}
          <div
            style={{
              borderTop: "1px solid #e5e7eb",
              marginTop: "8px",
              paddingTop: "16px",
            }}
          >
            {/* User Info and Settings Side by Side */}
            <div
              className="nn"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "12px 16px",
                background: "#f9fafb",
                borderRadius: "8px",
                marginBottom: "12px",
              }}
            >
              {/* User Info */}
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    color: "#1f2937",
                    fontWeight: "600",
                    fontSize: "14px",
                  }}
                >
                  {user?.first_name} {user?.last_name}
                </div>
                <div
                  style={{
                    color: "#6b7280",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {user?.roles?.[0] || user?.role || "User"} • Clinic:{" "}
                  {user?.clinic_id || "Main"}
                </div>
              </div>

              {/* Settings Button - Compact Version */}
              <button
                onClick={() => {
                  setIsSettingsOpen(true);
                  closeMobileMenu();
                }}
                style={{
                  padding: "8px 12px",
                  background: colorTheme.primary,
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: "600",
                  minHeight: "36px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  flexShrink: 0, // Prevent button from shrinking
                }}
              >
                <span style={{ fontSize: "14px" }}>⚙️</span>
                <span className="mobile-settings-text">Settings</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ minHeight: "calc(100vh - 64px)", padding: "16px" }}>
        <Routes>
          <Route path="/" element={<PerfectDashboard />} />
          <Route path="/appointments" element={<Appointments />} />
          <Route path="/patients" element={<Patients />} />
          <Route path="/patients/register" element={<Patients />} />
          <Route path="/patients/:patientId" element={<Patients />} />
          <Route path="/patients/:patientId/:tab" element={<Patients />} />
          <Route path="/services" element={<MedicalServices />} />
          <Route path="/transactions" element={<GeneralTransactionsPage />} />
          <Route path="/revenue" element={<RevenueDashboard />} />
          <Route
            path="/SecretaryManagement"
            element={<SecretaryManagement />}
          />
          {/* 404 redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>

      {/* Settings Modal */}
      <UserSettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          /* Base responsive utilities */
          * {
            box-sizing: border-box;
          }

          html {
            -webkit-overflow-scrolling: touch;
          }

          input, select, textarea {
            font-size: 16px;
          }
          /* Disable pull-to-refresh on mobile and tablet */
          @media (max-width: 1024px) {
            body {
              overscroll-behavior-y: contain;
              overscroll-behavior-x: none;
            }
            
            html {
              overscroll-behavior-y: contain;
              overscroll-behavior-x: none;
            }
          }
          /* Tablet Styles (769px - 1024px) */
          @media (min-width: 769px) and (max-width: 1024px) {
            /* Navigation adjustments for tablet */
            .desktop-nav {
              gap: 4px !important;
            }

            .desktop-nav button {
              padding: 8px 12px !important;
              min-width: 44px !important;
              font-size: 13px !important;
            }

            .nav-label {
              display: none !important;
            }

            /* User info adjustments */
            .desktop-user-info {
              font-size: 12px !important;
            }

            .desktop-user-info span:last-child {
              display: none !important;
            }

            .desktop-settings-btn {
              padding: 6px 12px !important;
              font-size: 12px !important;
            }

            /* Dashboard container adjustments */
            [style*="maxWidth"],
            [style*="max-width"] {
              max-width: 100% !important;
            }

            /* Header section adjustments */
            [style*="padding: 32px"] {
              padding: 24px 20px !important;
            }

            [style*="fontSize: 32px"] {
              font-size: 28px !important;
            }

            [style*="fontSize: 18px"] {
              font-size: 16px !important;
            }

            /* Metrics grid for tablet */
            [style*="grid-template-columns: repeat(auto-fit, minmax(200px, 1fr))"] {
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 16px !important;
            }

            /* Metric card adjustments */
            [style*="padding: 24px"][style*="borderRadius: 12px"] {
              padding: 20px !important;
            }

            [style*="fontSize: 28px"] {
              font-size: 24px !important;
            }

            [style*="fontSize: 14px"] {
              font-size: 13px !important;
            }

            [style*="fontSize: 12px"] {
              font-size: 11px !important;
            }

            /* Main content grid for tablet */
            [style*="grid-template-columns: 2fr 1fr"] {
              grid-template-columns: 1fr !important;
              gap: 20px !important;
            }

            /* Quick actions grid for tablet */
            [style*="grid-template-columns: 1fr 1fr"] {
              grid-template-columns: 1fr 1fr !important;
              gap: 10px !important;
            }

            /* Quick action button adjustments */
            [style*="padding: 16px 12px"] {
              padding: 14px 10px !important;
              font-size: 11px !important;
            }

            [style*="fontSize: 24px"][style*="marginBottom: 8px"] {
              font-size: 20px !important;
              margin-bottom: 6px !important;
            }

            /* System alerts adjustments */
            [style*="padding: 12px"][style*="borderRadius: 8px"] {
              padding: 10px !important;
            }

            /* Bottom section adjustments */
            [style*="gridTemplateColumns"][style*="1fr 1fr"] {
              grid-template-columns: 1fr !important;
              gap: 20px !important;
            }

            /* Activity item adjustments */
            [style*="padding: 12px 0"] {
              padding: 10px 0 !important;
            }

            /* Revenue card adjustments */
            [style*="padding: 16px"][style*="borderRadius: 12px"] {
              padding: 14px !important;
              margin-bottom: 12px !important;
            }

            [style*="fontSize: 24px"][style*="fontWeight: 700"] {
              font-size: 20px !important;
            }

            /* Performance summary adjustments */
            [style*="grid-template-columns: repeat(auto-fit, minmax(150px, 1fr))"],
            [style*="grid-template-columns: repeat(auto-fit, minmax(120px, 1fr))"] {
              grid-template-columns: repeat(3, 1fr) !important;
              gap: 16px !important;
            }

            [style*="fontSize: 20px"][style*="fontWeight: 700"] {
              font-size: 18px !important;
            }

            /* General card padding adjustments */
            [style*="borderRadius: 16px"][style*="padding: 24px"] {
              padding: 20px !important;
              margin-bottom: 16px !important;
            }

            /* Button adjustments */
            button {
              min-height: 40px !important;
              font-size: 13px !important;
            }

            [style*="padding: 8px 16px"] {
              padding: 8px 14px !important;
            }

            [style*="padding: 12px 24px"] {
              padding: 10px 20px !important;
            }
          }

          /* Large Tablet Styles (1025px - 1200px) */
          @media (min-width: 1025px) and (max-width: 1200px) {
            .desktop-nav {
              gap: 6px !important;
            }

            .desktop-nav button {
              padding: 8px 14px !important;
              font-size: 14px !important;
            }

            /* Show labels but with smaller text */
            .nav-label {
              display: inline !important;
              font-size: 13px !important;
            }

            /* Metrics grid for large tablet */
            [style*="grid-template-columns: repeat(auto-fit, minmax(200px, 1fr))"] {
              grid-template-columns: repeat(4, 1fr) !important;
              gap: 18px !important;
            }

            /* Main content grid for large tablet */
            [style*="grid-template-columns: 2fr 1fr"] {
              grid-template-columns: 1.8fr 1.2fr !important;
              gap: 22px !important;
            }

            [style*="maxWidth: 1400px"] {
              max-width: 1150px !important;
              padding: 0 20px !important;
            }
          }

          /* Mobile Styles (up to 768px) */
          @media (max-width: 768px) {
            /* Show mobile elements */
            .mobile-menu-btn-container {
              display: flex !important;
              position: relative !important;
              z-index: 10 !important;
            }

            .mobile-centered-logo {
              display: flex !important;
              position: absolute !important;
              left: 50% !important;
              top: 50% !important;
              transform: translate(-50%, -50%) !important;
              z-index: 5 !important;
            }

            /* Hide desktop elements */
            .desktop-logo {
              display: none !important;
            }

            .desktop-nav-container {
              display: none !important;
            }

            .desktop-nav {
              display: none !important;
            }

            .desktop-user-info,
            .desktop-settings-btn {
              display: none !important;
            }

            .desktop-user-section {
              display: none !important;
            }

            .nav-label {
              display: none !important;
            }

            /* Ensure nav container maintains flex layout */
            nav > div > div {
              display: flex !important;
              align-items: center !important;
              justify-content: space-between !important;
              position: relative !important;
            }

            /* Navigation specific rules - MUST stay horizontal */
            nav [style*="display: flex"][style*="justify-content: space-between"] {
              display: flex !important;
              flex-direction: row !important;
              align-items: center !important;
              justify-content: space-between !important;
              position: relative !important;
            }

            /* Force all dashboard content to stack vertically on mobile - EXCLUDING .keep-grid elements and nav */
            @media (max-width: 768px) {
  [style*="grid-template-columns"]:not(.keep-grid):not(.keep-grid *):not(nav *):not(.no):not(.no *),
  [style*="display: grid"]:not(.keep-grid):not(.keep-grid *):not(nav *):not(.no):not(.no *),
  .grid:not(.keep-grid):not(.keep-grid *):not(nav *):not(.no):not(.no *),
  .dashboard-grid:not(.keep-grid):not(.keep-grid *):not(nav *):not(.no):not(.no *) {
    display: flex !important;
    flex-direction: column !important;
    width: 100% !important;
    gap: 16px !important;
    grid-template-columns: none !important;
  }
}

            /* Rest of your existing mobile styles... */
            [style*="borderRadius: 12px"]:not(.appointment-calendar-container *):not([class*="calendar"]):not(nav *),
            [style*="borderRadius: 16px"]:not(.appointment-calendar-container *):not([class*="calendar"]):not(nav *):not(.no):not(.no *) {
              width: 100% !important;
              margin-bottom: 16px !important;
              border-radius: 12px !important;
              padding: 20px !important;
              box-sizing: border-box !important;
            }

            [style*="grid-template-columns: 1fr 1fr"]:not(.appointment-calendar-container *):not([class*="calendar"]):not(nav *) {
              display: flex !important;
              flex-direction: column !important;
              gap: 12px !important;
              width: 100% !important;
              grid-template-columns: none !important;
            }

            [style*="padding: 16px 12px"]:not(.appointment-calendar-container *):not(nav *) {
              width: 100% !important;
              min-height: 48px !important;
              padding: 12px 16px !important;
              font-size: 14px !important;
              border-radius: 8px !important;
              margin-bottom: 8px !important;
            }

            /* UPDATED: More specific exclusions for navigation */
            [style*="display: flex"]:not(.mobile-panel):not(.desktop-nav):not([class*="user"]):not(.tabs-container):not(.nav-controls):not(.appointment-calendar-container *):not([class*="calendar"]):not(nav):not(nav *):not(.mobile-hamburger-wrapper):not(.mobile-menu-btn-container):not(.mobile-centered-logo):not(.desktop-nav-container):not(.desktop-user-section):not(.nn) {
              flex-direction: column !important;
              align-items: stretch !important;
            }

            * {
              max-width: 100% !important;
              box-sizing: border-box !important;
            }

            /* Override for calendar to maintain horizontal scroll */
            .appointment-calendar-container * {
              max-width: initial !important;
            }
          }

          @media (min-width: 481px) and (max-width: 600px) {
            .mobile-settings-text::before {
              content: "Set";
            }
            
            .mobile-settings-text {
              display: none;
            }
            
            .mobile-settings-text::before {
              display: inline;
            }
          }

          /* Small mobile adjustments */
          @media (max-width: 480px) {
            h1 {
              font-size: 18px !important;
            }
            .mobile-settings-text {
              display: none !important;
            }
            
            /* Make settings button icon-only on very small screens */
            [style*="gap: 4px"][style*="flexShrink: 0"] {
              padding: 8px !important;
              min-width: 36px !important;
              gap: 0 !important;
            }
            [style*="padding: 32px"] {
              padding: 20px 16px !important;
            }

            [style*="fontSize: 32px"] {
              font-size: 24px !important;
            }

            [style*="fontSize: 18px"] {
              font-size: 14px !important;
            }

            [style*="padding: 24px"] {
              padding: 16px !important;
            }
          }

          /* Hide mobile elements on desktop and tablet */
          @media (min-width: 769px) {
            .mobile-menu-btn-container {
              display: none !important;
            }
            
            .mobile-panel {
              display: none !important;
              flex-direction: row !important;
            }

            .mobile-backdrop {
              display: none !important;
            }

            .mobile-centered-logo {
              display: none !important;
            }
          }

          /* Tablet-specific utility classes */
          @media (min-width: 769px) and (max-width: 1024px) {
            .tablet-hide {
              display: none !important;
            }

            .tablet-stack {
              flex-direction: column !important;
            }

            .tablet-full-width {
              width: 100% !important;
            }

            .tablet-compact {
              padding: 12px !important;
              margin: 8px 0 !important;
            }

            .tablet-small-text {
              font-size: 12px !important;
            }

            .tablet-medium-text {
              font-size: 14px !important;
            }
          }
        `}
      </style>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
};

export default App;
